// 1-A — MEMBER STATUS GUIDANCE의 multi-period 분기 부재로 인한 wrong intention 선택 차단.
//
// 문제 패턴 (L5-MEMBER-NEWMEMBER-001 / L5-MEMBERSHIP-TOPREREG-001 / L5-MEMBER-CHURN-001 등):
//   사용자: "1월 2월 3월 신규 가입 변화 비교해줘" / "1월 2월 3월 재등록 상위 회원 비교" / "이탈률 추이 비교"
//   → aiPrompts.js:441 MEMBER STATUS GUIDANCE는 "신규 가입/재등록/이탈" keyword를
//     CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST + show_member_status sequence로 라우팅
//   → classifyMemberStatus는 single-period only — multi-period 비교에 부적합
//   → LLM이 HISTORY_LIST × N month 호출 → 매 period 동일 entity (전체 회원) 반환
//
// 본질 차단:
//   call_api 진입 시 (a) intention이 visualization HISTORY_LIST + (b) input multi-period 감지
//   + (c) MEMBER STATUS GUIDANCE keyword 포함 → 정답 LIST_BY+PERIOD/STATISTICS_BY+PERIOD intention으로 자동 치환.
//   keyword set은 aiPrompts.js:441 GUIDANCE에서 정의된 keyword와 동기화 (가이드 + 매핑 single source).
//   per (intention, slot) 1회 자동 치환 한정 — 무한 재귀 차단.

const HEADER = '[MULTI-PERIOD MEMBER STATUS]';

// HISTORY_LIST visualization intention (separator 변형 포함).
// MEMBER STATUS GUIDANCE의 SEQUENCE step (1) intention.
const WRONG_INTENTION_KEYS = new Set([
  'CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST',
  'CHECK_MEMBER_MEMBERSHIP_HISTORY_LIST',
]);

// MEMBER STATUS GUIDANCE의 keyword → 정답 multi-period intention 명시 매핑.
// 가이드 정의된 keyword set과 동기화 — 가이드가 single-period로 라우팅하는 keyword가
// multi-period 입력일 때 LIST_BY+PERIOD/STATISTICS_BY+PERIOD intention으로 자동 치환.
// 우선순위 순 — "재등록 상위"는 "재등록 회원" 매칭 전에 평가되어야 TOP intention 정확 선택.
const KEYWORD_TO_INTENTION = Object.freeze([
  {
    label: '신규 가입',
    keywords: ['신규 가입', '신규 회원', '신규 가입자', '신규가입'],
    intention: 'CHECK_NEW+MEMBER_LIST_BY+PERIOD',
  },
  {
    label: '재등록 상위 (TOP)',
    keywords: ['재등록 상위', '재등록 TOP', '재등록상위'],
    intention: 'CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD',
  },
  {
    label: '재등록률 (RATE)',
    keywords: ['재등록률', '재등록 추이', '재등록률 추이', '재등록 회원'],
    intention: 'CHECK_MEMBERSHIP+RE+REGISTRATION+RATE_STATISTICS_BY+PERIOD',
  },
  {
    label: '이탈률/잔존율',
    keywords: ['이탈률', '이탈 회원', '잔존율', '유지율', '이탈률 추이'],
    intention: 'CHECK_MEMBER+CHURN+RETENTION+RATE_STATISTICS_BY+PERIOD',
  },
]);

/**
 * text에서 "N월" 추출 → Set 반환 (AgentSession._extractMonthSet과 동일 로직).
 * pure 형태로 분리 — module 외부 의존성 없이 재사용.
 *
 * @param {string} text
 * @returns {Set<string>}
 */
export function extractMonthSet(text) {
  const set = new Set();
  if (typeof text !== 'string') return set;
  for (const m of text.matchAll(/(\d{1,2})\s*월/g)) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 12) set.add(`${n}월`);
  }
  return set;
}

/**
 * 사용자 input + LLM이 호출한 intention 결합 분석.
 * multi-period (N월 2+) + MEMBER STATUS keyword + HISTORY_LIST intention 동시 만족 시
 * 자동 치환 대상 intention 반환.
 *
 * @param {object} ctx
 * @param {string} ctx.userInput
 * @param {string} ctx.intentionKey  — resolved canonical intention key
 * @returns {{
 *   replacementIntention: string,
 *   matchedKeyword: string,
 *   matchedLabel: string,
 *   monthCount: number,
 * } | null}
 */
export function detectMultiPeriodMemberStatus(ctx = {}) {
  const { userInput, intentionKey } = ctx;
  if (!userInput || typeof userInput !== 'string') return null;
  if (!intentionKey || !WRONG_INTENTION_KEYS.has(intentionKey)) return null;

  const months = extractMonthSet(userInput);
  if (months.size < 2) return null;

  for (const mapping of KEYWORD_TO_INTENTION) {
    const matchedKeyword = mapping.keywords.find(k => userInput.includes(k));
    if (matchedKeyword) {
      return {
        replacementIntention: mapping.intention,
        matchedKeyword,
        matchedLabel: mapping.label,
        monthCount: months.size,
      };
    }
  }
  return null;
}

/**
 * 자동 치환 발동 시 LLM에게 보여줄 안내 prefix (재귀 호출 결과에 prepend).
 *
 * @param {object} ctx
 * @returns {string}
 */
export function formatReplacementMessage(ctx) {
  const { originalIntention, replacementIntention, matchedKeyword, matchedLabel, monthCount } = ctx;
  return (
    `${HEADER} "${originalIntention}"은(는) MEMBER STATUS GUIDANCE의 single-period sequence intention입니다. ` +
    `사용자 input에 N월 enumeration (${monthCount}개) + "${matchedKeyword}" keyword 감지 ` +
    `→ multi-period 비교 의도로 "${replacementIntention}" 자동 치환 ("${matchedLabel}" 카테고리).`
  );
}

// ────────────────────────────────────────────────
// SINGLE-PERIOD NEW-MEMBER MISROUTE — "이번 달 신규 회원" → ALIVE_LIST 오라우팅 차단
// ────────────────────────────────────────────────
//
// 문제 패턴 (운영 rid188 run2/5):
//   사용자: "이번 달 신규 회원 보여줘/확인해줘" (single-period)
//   → LLM이 CHECK_MEMBER+MEMBERSHIP+HISTORY_LIST 호출 후 show_member_status(new) 분류를 건너뛰고
//     CHECK_MEMBER+ALIVE_LIST(유효회원 전체 92명) 호출 → "신규 회원 92명" 거짓 라벨 (유효 전체를 신규로).
//   → multi-period 가드(위)는 N월 2+만 커버 → single-period "이번 달 신규" 미차단.
//
// 본질 차단:
//   call_api 진입 시 (a) intention == ALIVE_LIST + (b) input "신규 회원/가입" keyword +
//   (c) "유효/활성/현재/전체" 미포함(ALIVE 정당 보존) + (d) single-period(N월 0~1, multi는 위 가드)
//   → CHECK_NEW+MEMBER_LIST_BY+PERIOD(신규 회원 명단) 자동 치환. per (intention, slot) 1회.
//   show_member_status 분류 경로(HISTORY → show_member_status)는 intention이 ALIVE 아니라 미발동.

const ALIVE_INTENTION_KEYS = new Set([
  'CHECK_MEMBER+ALIVE_LIST',
  'CHECK_MEMBER_ALIVE_LIST',
]);
const NEW_MEMBER_KEYWORDS = ['신규 회원', '신규회원', '신규 가입', '신규가입', '신규 가입자'];
const ACTIVE_INTENT_KEYWORDS = ['유효', '활성', '현재 회원', '현재회원', '전체 회원', '전체회원'];

/**
 * 사용자 input + LLM이 호출한 intention 결합 분석.
 * single-period 신규 회원 요청인데 ALIVE_LIST(유효 전체)로 오라우팅 시 CHECK_NEW 치환 대상 반환.
 *
 * @param {object} ctx
 * @param {string} ctx.userInput
 * @param {string} ctx.intentionKey  — resolved canonical intention key
 * @returns {{ replacementIntention: string, matchedKeyword: string } | null}
 */
export function detectNewMemberMisroute(ctx = {}) {
  const { userInput, intentionKey } = ctx;
  if (!userInput || typeof userInput !== 'string') return null;
  if (!intentionKey || !ALIVE_INTENTION_KEYS.has(intentionKey)) return null;

  // "유효/활성/현재/전체" 명시 → ALIVE 정당, 미발동
  if (ACTIVE_INTENT_KEYWORDS.some(k => userInput.includes(k))) return null;

  const matchedKeyword = NEW_MEMBER_KEYWORDS.find(k => userInput.includes(k));
  if (!matchedKeyword) return null;

  // multi-period(N월 2+)는 위 multi-period 가드 영역 — single-period만 처리
  if (extractMonthSet(userInput).size >= 2) return null;

  return {
    replacementIntention: 'CHECK_NEW+MEMBER_LIST_BY+PERIOD',
    matchedKeyword,
  };
}

/**
 * 자동 치환 발동 시 LLM에게 보여줄 안내 prefix.
 *
 * @param {object} ctx
 * @returns {string}
 */
export function formatNewMemberMisrouteMessage(ctx) {
  const { originalIntention, replacementIntention, matchedKeyword } = ctx;
  return (
    `${HEADER} "${originalIntention}"은(는) 유효회원 전체 목록(신규 아님)입니다. ` +
    `사용자 input "${matchedKeyword}" (신규 회원 명단 요청) 감지 → "${replacementIntention}"으로 자동 치환.`
  );
}

export const _HEADER = HEADER;
export const _KEYWORD_TO_INTENTION = KEYWORD_TO_INTENTION;
export const _WRONG_INTENTION_KEYS = WRONG_INTENTION_KEYS;
export const _ALIVE_INTENTION_KEYS = ALIVE_INTENTION_KEYS;
export const _NEW_MEMBER_KEYWORDS = NEW_MEMBER_KEYWORDS;
